import { timeOf } from '@shkola/api';
import { type ToneName, subjectLook, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import {
  type CSSProperties,
  type ComponentType,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowCounterClockwiseIcon,
  ArrowUpRightIcon,
  CaretRightIcon,
  CheckCircleIcon,
  ExamIcon,
  type FilledIcon,
  FlagIcon,
  MegaphoneIcon,
  PaperPlaneTiltIcon,
  WarningCircleIcon,
} from '../../design/icons';
import { useLook } from '../../design/looks';
import { OnColorProvider, StatusPill } from '../../design/ui';
import { AlarmArt, DoneArt, ExamArt, FlagArt, MegaphoneArt, PlaneArt, ReworkArt } from './Art';
import {
  type StudentContext,
  type TodoItem,
  type TodoLink,
  dayMonthShort,
  useTodo,
  weekdayKey,
  weekdayShort,
} from './model';

/*
 * «Треба зробити» as cards (UX of 26.10 on the look of 27.10): one card per kind of thing to do, in
 * the order of urgency — what is late, what to hand in soon, the tests of the week, the works to redo,
 * the announcements to confirm, the missed lessons. Each card says how many in a big number (a number
 * that asks for action, never a statistic), shows the first few with the one action each and has its
 * picture; an empty kind has no card, and when there is nothing at all — one card «Усе зроблено».
 * The frame follows the look: «Небо» — the tone's gradient with a white rim, «Тепло» — a white card
 * with a coloured panel inside. «Свіжість» puts the same rows into cards of its own (FreshHome).
 */

type ArtComponent = ComponentType<{ tone: ToneName; className?: string }>;

const panelGround = (tone: ToneName) =>
  `linear-gradient(180deg, ${tones[tone].to} 0%, ${tones[tone].from} 100%)`;
const cardGround = (tone: ToneName) =>
  `linear-gradient(160deg, ${tones[tone].from} 0%, ${tones[tone].to} 100%)`;
/** «Свіжість»: the tone brighter, a white glow in the corner. */
export const boldGround = (tone: ToneName) =>
  `radial-gradient(90% 70% at 100% 0%, rgb(255 255 255 / 0.55) 0%, rgb(255 255 255 / 0) 60%), linear-gradient(150deg, ${tones[tone].fill} 0%, ${tones[tone].from} 100%)`;

export function TodoLinkTo({
  link,
  className,
  label,
  children,
}: {
  link: TodoLink;
  className: string;
  label?: string;
  children: ReactNode;
}) {
  if (link.to === '/homework')
    return (
      <Link
        to="/homework"
        search={link.course ? { course: link.course } : {}}
        className={className}
        aria-label={label}
        title={label}
      >
        {children}
      </Link>
    );
  return (
    <Link to={link.to} className={className} aria-label={label} title={label}>
      {children}
    </Link>
  );
}

/** The ↗ of a card: into its section. */
export function OpenLink({
  link,
  label,
  outline,
}: {
  link: TodoLink;
  label: string;
  outline: boolean;
}) {
  return (
    <TodoLinkTo
      link={link}
      label={label}
      className={`inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-ink transition-transform duration-500 ease-spring hover:rotate-45 ${outline ? 'ring-1 ring-stroke ring-inset' : ''}`}
    >
      <ArrowUpRightIcon size={15} weight="bold" aria-hidden />
    </TodoLinkTo>
  );
}

/** A card of the look: the icon and the title, the big number with its picture, the rows. */
function Card({
  tone,
  icon: Icon,
  title,
  link,
  art: Picture,
  lead,
  children,
  wide = false,
  className = '',
}: {
  tone: ToneName;
  icon: FilledIcon;
  title: string;
  link: TodoLink;
  art: ArtComponent;
  lead: ReactNode;
  children?: ReactNode;
  /** alone in its row on a computer: the number, the rows and the picture side by side */
  wide?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const look = useLook();
  const tint = tones[tone];
  const warm = look === 'warm';
  const head = (
    <header className="flex min-h-8 items-center gap-2">
      <span
        className="flex size-7 shrink-0 items-center justify-center rounded-[9px]"
        style={{ backgroundColor: warm ? tint.soft : '#FFFFFF', color: tint.vivid }}
      >
        <Icon size={16} weight="fill" aria-hidden />
      </span>
      <h2 className="min-w-0 truncate text-[15px] leading-5 font-semibold text-ink">{title}</h2>
      <span className="ml-auto">
        <OpenLink link={link} label={t('studentHome.cards.open')} outline={warm} />
      </span>
    </header>
  );
  const body = wide ? (
    <div className="relative flex flex-1 flex-col gap-3 xl:flex-row xl:items-end xl:gap-6 xl:pr-[132px]">
      <Picture
        tone={tone}
        className="absolute -top-3 -right-2 w-[112px] xl:top-auto xl:-bottom-2"
      />
      <div className="relative min-h-[88px] pr-[108px] xl:w-[300px] xl:shrink-0 xl:pr-0">
        {lead}
      </div>
      {children && (
        <OnColorProvider value={false}>
          <div className="relative flex min-w-0 flex-1 flex-col gap-1.5">{children}</div>
        </OnColorProvider>
      )}
    </div>
  ) : (
    <>
      <div className="relative min-h-[88px] pr-[108px]">
        <Picture tone={tone} className="absolute -top-3 -right-2 w-[112px]" />
        {lead}
      </div>
      {children && (
        <OnColorProvider value={false}>
          <div className="relative flex min-h-0 flex-1 flex-col gap-1.5">{children}</div>
        </OnColorProvider>
      )}
    </>
  );
  if (warm)
    return (
      <section
        className={`flex min-w-0 flex-col gap-2.5 rounded-[28px] bg-white p-2 pt-3 ring-1 ring-[#EFE8E3] ring-inset ${className}`}
      >
        <div className="px-2.5">{head}</div>
        <OnColorProvider value>
          <div
            className="relative flex min-h-0 flex-1 flex-col gap-3 overflow-hidden rounded-[20px] p-4"
            style={{ backgroundImage: panelGround(tone) }}
          >
            {body}
          </div>
        </OnColorProvider>
      </section>
    );
  return (
    <OnColorProvider value>
      <section
        className={`rim relative flex min-w-0 flex-col gap-3 overflow-hidden rounded-[28px] p-4 md:p-5 ${className}`}
        style={{ backgroundImage: cardGround(tone) }}
      >
        {head}
        {body}
      </section>
    </OnColorProvider>
  );
}

/** The big number and what it counts. */
function Lead({ tone, value, caption }: { tone: ToneName; value: ReactNode; caption: ReactNode }) {
  return (
    <div className="flex min-h-[88px] flex-wrap items-end gap-x-2.5 gap-y-1 pb-1">
      <span className="font-display text-[48px] leading-[52px] font-semibold tracking-[-0.03em] text-ink">
        {value}
      </span>
      <span className="pb-2 text-[14px] leading-5 font-medium" style={{ color: tones[tone].ink }}>
        {caption}
      </span>
    </div>
  );
}

/** «термін минув 28 жовт.», «сьогодні», «завтра», «у четвер» (+ «о 12:25» for a test). */
function when(t: TFunction, item: TodoItem, today: string, tomorrow: string): string {
  if (!item.due) return '';
  const day =
    item.due < today
      ? t('studentHome.todo.overdueSince', { date: dayMonthShort(item.due) })
      : item.due === today
        ? t('studentHome.todo.today')
        : item.due === tomorrow
          ? t('studentHome.todo.tomorrow')
          : t(`student.on.${weekdayKey(item.due)}`);
  return item.time ? `${day} ${t('studentHome.todo.at', { time: timeOf(item.time) })}` : day;
}

/** A thing to do on a white row: the subject's colour, what and when, the one action. */
export function TodoRow({
  item,
  tone,
  today,
  tomorrow,
  lead,
}: {
  item: TodoItem;
  tone: ToneName;
  today: string;
  tomorrow: string;
  /** in front of the text instead of the subject's bar (the date of a test) */
  lead?: ReactNode;
}) {
  const { t } = useTranslation();
  const subject = item.kind === 'test' ? item.title : item.subject;
  const bar = subject ? tones[subjectLook(subject).tone].icon : tones[tone].icon;
  const due = item.kind === 'rework' || item.kind === 'ack' ? '' : when(t, item, today, tomorrow);
  const meta = [
    item.kind === 'quiz' ? t('studentHome.todo.quiz') : item.kind === 'test' ? '' : item.subject,
    item.kind === 'ack' ? t('studentHome.todo.announcement') : undefined,
    item.note ? `«${item.note}»` : undefined,
  ].filter(Boolean);
  return (
    <TodoLinkTo
      link={item.link}
      className="group flex min-h-14 min-w-0 items-center gap-3 rounded-[16px] bg-white py-2 pr-3 pl-2.5 transition-shadow duration-200 hover:shadow-[0_2px_12px_rgb(23_23_27/0.08)]"
    >
      {lead ?? (
        <span className="w-1 shrink-0 self-stretch rounded-full" style={{ backgroundColor: bar }} />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] leading-5 font-semibold text-ink">
          {item.title}
        </span>
        {/* a test: when first, its topic after it */}
        <span
          className={`flex min-w-0 gap-1 text-[12.5px] leading-[18px] text-muted ${item.kind === 'test' ? 'flex-row-reverse justify-end' : ''}`}
        >
          {meta.length > 0 && (
            <span className="min-w-0 truncate">
              {meta.join(' · ')}
              {due && (item.kind === 'test' ? '' : ' ·')}
            </span>
          )}
          {due && (
            <span
              className="shrink-0 font-medium"
              style={{
                color:
                  item.group === 'overdue'
                    ? tones.red.vivid
                    : item.group === 'today' || item.group === 'tomorrow'
                      ? tones.blue.vivid
                      : undefined,
              }}
            >
              {due}
              {item.kind === 'test' && meta.length > 0 && ' ·'}
            </span>
          )}
        </span>
      </span>
      <span
        className="flex shrink-0 items-center gap-0.5 text-[13px] font-semibold"
        style={{ color: tones[tone].vivid }}
      >
        {/* in a narrow card («Свіжість», a container under 20rem) only the arrow */}
        <span className="@max-xs:sr-only">{t(`studentHome.todo.do.${item.kind}`)}</span>
        <CaretRightIcon
          size={12}
          weight="bold"
          aria-hidden
          className="transition-transform group-hover:translate-x-0.5"
        />
      </span>
    </TodoLinkTo>
  );
}

/** «ще 28 →» under the rows. */
export function More({ count, link, tone }: { count: number; link: TodoLink; tone: ToneName }) {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <TodoLinkTo
      link={link}
      className="mt-0.5 inline-flex items-center gap-1 self-start px-1 text-[13px] font-semibold hover:underline"
    >
      <span style={{ color: tones[tone].ink }}>{t('studentHome.todo.more', { count })}</span>
      <CaretRightIcon size={12} weight="bold" aria-hidden color={tones[tone].ink} />
    </TodoLinkTo>
  );
}

/** A date as two tiles, «ср» «4» (ref. «Day in history»). */
export function DateTiles({
  date,
  size = 'lg',
  tone,
}: {
  date: string;
  size?: 'lg' | 'sm';
  tone: ToneName;
}) {
  const box =
    size === 'lg'
      ? 'h-12 min-w-12 rounded-[14px] text-[20px] shadow-[0_1px_2px_rgb(23_23_27/0.06)]'
      : 'h-9 min-w-9 rounded-[10px] text-[15px]';
  const ground = size === 'lg' ? '#FFFFFF' : tones[tone].soft;
  return (
    <span className="flex shrink-0 items-center gap-1" aria-hidden>
      <span
        className={`flex items-center justify-center px-2 font-display font-semibold ${box}`}
        style={{ backgroundColor: ground, color: tones[tone].ink }}
      >
        {weekdayShort(date)}
      </span>
      <span
        className={`flex items-center justify-center px-2 font-display font-semibold text-ink ${box}`}
        style={{ backgroundColor: ground }}
      >
        {Number(date.slice(8))}
      </span>
    </span>
  );
}

// —— the cards ——

/** A row of a card is 56 px high, 6 px apart. */
const ROW = 56;
const GAP = 6;

/**
 * How many rows fit the height the list is given (a card stretched to the height of the day): the
 * list fills it instead of leaving an empty bottom. Never fewer than `min`.
 */
function useFit(min: number) {
  const ref = useRef<HTMLUListElement>(null);
  const [count, setCount] = useState(min);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry)
        setCount(Math.max(min, Math.floor((entry.contentRect.height + GAP) / (ROW + GAP))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return [ref, count] as const;
}

/**
 * The rows of a card and «ще N» under them. With `fill` (beside the day on a computer) the list takes
 * the height left in its card and shows as many rows as fit.
 */
function Rows({
  items,
  min,
  fill,
  link,
  tone,
  row,
}: {
  items: TodoItem[];
  min: number;
  fill: boolean;
  link: TodoLink;
  tone: ToneName;
  row: (item: TodoItem) => ReactNode;
}) {
  const [ref, fit] = useFit(min);
  const count = fill ? fit : min;
  return (
    <>
      <ul
        ref={ref}
        className={`flex flex-col gap-1.5 ${fill ? 'xl:min-h-(--rows) xl:flex-1 xl:basis-0 xl:overflow-hidden' : ''}`}
        style={
          { '--rows': `${Math.min(min, items.length) * (ROW + GAP) - GAP}px` } as CSSProperties
        }
      >
        {items.slice(0, count).map((item) => (
          <li key={item.key}>{row(item)}</li>
        ))}
      </ul>
      <More count={items.length - count} link={link} tone={tone} />
    </>
  );
}

/** How tall a card is by itself: the head, the number and its picture, the rows, «ще N». */
const cardHeight = (rows: number, more: boolean, extra = 0) =>
  32 + 12 + 88 + (rows > 0 ? 12 + rows * (ROW + GAP) - GAP : 0) + (more ? 26 : 0) + extra + 40;

const SHOWN = { overdue: 3, soon: 3, tests: 2, rework: 2, ack: 2 } as const;

export interface TodoCard {
  key: string;
  /** its height by itself, px: to choose which cards fit beside the day */
  height: number;
  /** it has more rows than it shows: beside the day it can take the height left */
  grows: boolean;
  /** `fill` — beside the day, taking the height left; `wide` — alone in its row */
  render: (fill: boolean, wide: boolean) => ReactNode;
}

/** The cards of what to do, in the order of urgency. */
export function useTodoCards(ctx: StudentContext) {
  const { t } = useTranslation();
  const todo = useTodo(ctx);
  const { today, tomorrow } = todo;
  const of = (group: TodoItem['group']) => todo.groups.find((g) => g.group === group)?.items ?? [];
  const overdue = of('overdue');
  const soon = [...of('today'), ...of('tomorrow'), ...of('week')].filter((i) => i.kind !== 'test');
  const tests = [...of('today'), ...of('tomorrow'), ...of('week')].filter((i) => i.kind === 'test');
  const rework = of('rework');
  const ack = of('ack');
  const catchUp = of('catchUp')[0];
  const cards: TodoCard[] = [];

  /** A card of a list: the number, the first rows, the rest behind «ще N». */
  const list = (
    key: string,
    items: TodoItem[],
    min: number,
    card: Omit<Parameters<typeof Card>[0], 'children'>,
    row: (item: TodoItem) => ReactNode = (item) => (
      <TodoRow item={item} tone={card.tone} today={today} tomorrow={tomorrow} />
    ),
  ) =>
    cards.push({
      key,
      height: cardHeight(Math.min(min, items.length), items.length > min),
      grows: items.length > min,
      render: (fill, wide) => (
        <Card {...card} wide={wide}>
          {items.length > 0 && (
            <Rows items={items} min={min} fill={fill} link={card.link} tone={card.tone} row={row} />
          )}
        </Card>
      ),
    });

  if (overdue.length > 0)
    list('overdue', overdue, SHOWN.overdue, {
      tone: 'red',
      icon: WarningCircleIcon,
      title: t('studentHome.cards.overdue.title'),
      link: { to: '/homework' },
      art: AlarmArt,
      lead: (
        <Lead
          tone="red"
          value={overdue.length}
          caption={t('studentHome.cards.overdue.caption', { count: overdue.length })}
        />
      ),
    });

  if (soon.length > 0) {
    const onToday = soon.filter((i) => i.due === today).length;
    const onTomorrow = soon.filter((i) => i.due === tomorrow).length;
    const [value, caption] =
      onToday > 0
        ? [onToday, t('studentHome.cards.soon.today', { count: onToday })]
        : onTomorrow > 0
          ? [onTomorrow, t('studentHome.cards.soon.tomorrow', { count: onTomorrow })]
          : [soon.length, t('studentHome.cards.soon.week', { count: soon.length })];
    list('soon', soon, SHOWN.soon, {
      tone: 'blue',
      icon: PaperPlaneTiltIcon,
      title: t('studentHome.cards.soon.title'),
      link: { to: '/homework' },
      art: PlaneArt,
      lead: <Lead tone="blue" value={value} caption={caption} />,
    });
  }

  if (tests.length > 0) {
    const [first, ...others] = tests as [TodoItem, ...TodoItem[]];
    list(
      'tests',
      others,
      SHOWN.tests,
      {
        tone: 'violet',
        icon: ExamIcon,
        title: t('studentHome.cards.tests.title'),
        link: { to: '/schedule' },
        art: ExamArt,
        lead: (
          <div className="flex min-h-[88px] flex-col justify-end gap-2.5 pb-1">
            <DateTiles date={first.due ?? today} tone="violet" />
            <span className="min-w-0">
              <span className="block truncate text-[16px] leading-6 font-semibold text-ink">
                {first.title}
                {first.time && (
                  <span className="font-medium" style={{ color: tones.violet.ink }}>
                    {' '}
                    · {when(t, first, today, tomorrow)}
                  </span>
                )}
              </span>
              {first.note && (
                <span
                  className="block truncate text-[13px] leading-5"
                  style={{ color: tones.violet.ink }}
                >
                  {first.note}
                </span>
              )}
            </span>
          </div>
        ),
      },
      (item) => (
        <TodoRow
          item={item}
          tone="violet"
          today={today}
          tomorrow={tomorrow}
          lead={<DateTiles date={item.due ?? today} size="sm" tone="violet" />}
        />
      ),
    );
  }

  if (rework.length > 0)
    list('rework', rework, SHOWN.rework, {
      tone: 'orange',
      icon: ArrowCounterClockwiseIcon,
      title: t('studentHome.cards.rework.title'),
      link: { to: '/homework' },
      art: ReworkArt,
      lead: (
        <Lead
          tone="orange"
          value={rework.length}
          caption={t('studentHome.cards.rework.caption', { count: rework.length })}
        />
      ),
    });

  if (ack.length > 0)
    list('ack', ack, SHOWN.ack, {
      tone: 'yellow',
      icon: MegaphoneIcon,
      title: t('studentHome.cards.ack.title'),
      link: { to: '/announcements' },
      art: MegaphoneArt,
      lead: (
        <Lead
          tone="yellow"
          value={ack.length}
          caption={t('studentHome.cards.ack.caption', { count: ack.length })}
        />
      ),
    });

  if (catchUp)
    cards.push({
      key: 'catchUp',
      height: cardHeight(0, false, 12 + 24 + 6 + 48),
      grows: false,
      render: (_, wide) => (
        <Card
          wide={wide}
          tone="teal"
          icon={FlagIcon}
          title={t('studentHome.cards.catchUp.title')}
          link={{ to: '/catch-up' }}
          art={FlagArt}
          lead={
            <Lead
              tone="teal"
              value={catchUp.count ?? 0}
              caption={t('studentHome.cards.catchUp.caption', { count: catchUp.count ?? 0 })}
            />
          }
        >
          <div className="flex flex-wrap gap-1.5">
            <OnColorProvider value>
              {(catchUp.subjects ?? []).slice(0, 4).map((s) => (
                <StatusPill key={s} tone="teal" size="sm">
                  {s}
                </StatusPill>
              ))}
            </OnColorProvider>
          </div>
          <Link
            to="/catch-up"
            className="group mt-auto flex min-h-12 items-center gap-2 rounded-[16px] bg-white px-3.5 text-[13.5px] font-semibold transition-shadow hover:shadow-[0_2px_12px_rgb(23_23_27/0.08)]"
            style={{ color: tones.teal.vivid }}
          >
            <span className="min-w-0 flex-1 truncate text-ink">
              {t('studentHome.cards.catchUp.hint')}
            </span>
            {t('studentHome.todo.do.catchUp')}
            <CaretRightIcon size={12} weight="bold" aria-hidden />
          </Link>
        </Card>
      ),
    });

  if (!todo.pending && cards.length === 0)
    cards.push({
      key: 'done',
      height: cardHeight(0, false),
      grows: false,
      render: (_, wide) => (
        <Card
          wide={wide}
          tone="green"
          icon={CheckCircleIcon}
          title={t('studentHome.cards.done.title')}
          link={{ to: '/homework' }}
          art={DoneArt}
          lead={
            <div className="flex min-h-[88px] flex-col justify-end gap-1 pb-1">
              <span className="font-display text-[24px] leading-8 font-semibold text-ink">
                {t('studentHome.cards.done.title')}
              </span>
              <span className="text-[14px] leading-5" style={{ color: tones.green.ink }}>
                {t('studentHome.cards.done.text')}
              </span>
            </div>
          }
        />
      ),
    });

  return { pending: todo.pending, cards };
}
